import _ from 'lodash';
import qs from 'qs';
import moment from 'moment';
import { safeParseInt } from '../../../../common/utils';
import { BracketFormat, IPublishedBracketSummary } from '../../../../types/eventBracketTypes';
import { getEventsLeaderboardWidgetQueryParams } from '../../events/utils/utils';
import { DYNAMIC_BRACKET_EVENT_START } from '../../../../gameConfig/mythicPlus';
import { TKeystoneTeamRankings } from '../../../../types/keystoneRankings';
import { LiveMastheadComponents, TLiveMastheadComponentConfig } from '../liveEventMasthead/types';
import { SHOW_DUNGEONS_IN_LEADERBOARD_RUNS_BRACKET_CARD_CONTENT } from '../liveEventMasthead/constants';
import { commonApiServiceAsync, retryableCommonApiServiceAsync } from '../../services/commonApiService';

interface IGetRunRankingsDataParams {
  bracket: IPublishedBracketSummary;
  eventSlug: string;
  rankingsLimit?: number;
  eventConfig?: (TLiveMastheadComponentConfig & { component: LiveMastheadComponents.Event }) | null;
}

export const getRunRankingsData = async (
  { bracket, eventSlug, rankingsLimit, eventConfig }: IGetRunRankingsDataParams,
  config?: {
    retry?: boolean;
  }
): Promise<{
  rankingsByTeam: TKeystoneTeamRankings;
  rankingsByDungeon: any;
}> => {
  const GROUPS_COUNT_TO_FETCH = 1;
  const { retry = false } = config || {};
  const apiService = retry ? retryableCommonApiServiceAsync : commonApiServiceAsync;

  const qParams = qs.parse(getEventsLeaderboardWidgetQueryParams({ bracketDetails: bracket, eventSlug }));

  const shouldReturnEmptyRankings =
    (moment.utc(bracket.bracketStartsDate).isAfter() && eventConfig?.timer !== 'never') ||
    eventConfig?.timer === 'always';

  if (shouldReturnEmptyRankings) {
    return {
      rankingsByTeam: {
        bestTeam: null,
        placeholderTeams: [],
        qualifyingTeam: [],
        rankedTeams: [],
        region: null,
        ui: {}
      },
      rankingsByDungeon: []
    };
  }

  const [rankingsByTeam, rankingsByDungeon] = await Promise.all([
    apiService
      .getKeystoneTeamRankings({
        page: 0,
        pageSize: rankingsLimit,
        region: 'world',
        ..._.pick(qParams, 'eventId', 'season')
      })
      .then((data) => data.rankings),
    SHOW_DUNGEONS_IN_LEADERBOARD_RUNS_BRACKET_CARD_CONTENT
      ? Promise.all(
          bracket.dungeons.map(async (map: any) => {
            const { rankings } = await apiService.getKeystoneRunRankings({
              season: bracket.rulesetSeasonSlug!,
              region: 'world',
              dungeon: map.dungeon.slug,
              page: 0,
              limit: GROUPS_COUNT_TO_FETCH,
              minMythicLevel: safeParseInt(bracket?.rulesetMinKeystoneLevel?.toString() || '0', 0),
              maxMythicLevel: safeParseInt(bracket?.rulesetMaxKeystoneLevel?.toString() || '0', 0),
              eventId: DYNAMIC_BRACKET_EVENT_START + bracket.id
            });
            return rankings;
          })
        )
      : Promise.resolve([])
  ]);

  return { rankingsByTeam, rankingsByDungeon };
};

export const isBracketFormatEligibleForRankings = (bracket: { format: BracketFormat }) => {
  return [BracketFormat.THE_GREAT_PUSH, BracketFormat.TIME_TRIALS].includes(bracket.format);
};
